import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Upload, Image, Eye, EyeOff, Play, Video } from "lucide-react";
import axios from "axios";
import { formatApiError, API, authHeaders } from "@/hooks/useAdminData";

function PosterPreview({ poster }) {
  if (!poster.image_url && !poster.video_url) return null;
  const isVideo = poster.media_type === "video";

  return (
    <div className="rounded-xl overflow-hidden mb-4 max-w-md" style={{ backgroundColor: poster.bg_color }}>
      {isVideo && poster.video_url ? (
        poster.video_url.includes("youtube.com") || poster.video_url.includes("youtu.be") ? (
          <div className="relative aspect-video">
            <iframe
              src={`https://www.youtube.com/embed/${extractYouTubeId(poster.video_url)}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={poster.alt_text || "Event video"}
            />
          </div>
        ) : (
          <video src={poster.video_url} className="w-full h-auto" controls preload="metadata" />
        )
      ) : poster.image_url ? (
        <img src={poster.image_url} alt={poster.alt_text || "Event poster"} className="w-full h-auto" />
      ) : null}
    </div>
  );
}

function extractYouTubeId(url) {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : "";
}

export default function EventPosterManager({ siteSettings, loadData }) {
  const [poster, setPoster] = useState({
    active: false, image_url: "", video_url: "", media_type: "image",
    alt_text: "", link: "/events", bg_color: "#2D1B69"
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (siteSettings.event_poster) setPoster(prev => ({ ...prev, ...siteSettings.event_poster }));
  }, [siteSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/settings/upload-poster`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
      });
      loadData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, { event_poster: poster }, { withCredentials: true, headers: authHeaders() });
      loadData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    const updated = { ...poster, active: !poster.active };
    setPoster(updated);
    try {
      await axios.put(`${API}/settings`, { event_poster: updated }, { withCredentials: true, headers: authHeaders() });
      loadData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const setMediaType = (type) => {
    setPoster(prev => ({ ...prev, media_type: type }));
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="event-poster-manager">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Image size={18} className="text-[#E8922F]" />
          <h4 className="font-semibold text-[#1A1A1A]">Event Poster / Video on Homepage</h4>
        </div>
        <div className="flex items-center gap-2">
          {poster.active ? (
            <span className="text-xs text-green-600 flex items-center gap-1"><Eye size={12} /> Visible</span>
          ) : (
            <span className="text-xs text-gray-400 flex items-center gap-1"><EyeOff size={12} /> Hidden</span>
          )}
          <Switch checked={poster.active} onCheckedChange={toggleActive} data-testid="poster-toggle" />
        </div>
      </div>

      <p className="text-[#4A4A4A] text-xs mb-4">Upload a photo poster or video to display on the homepage. Toggle it on/off as needed.</p>

      {/* Media type selector */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setMediaType("image")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors ${poster.media_type === "image" ? "bg-[#E8922F] text-black" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
          data-testid="poster-type-image"
        >
          <Image size={14} /> Photo Poster
        </button>
        <button
          onClick={() => setMediaType("video")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors ${poster.media_type === "video" ? "bg-[#E8922F] text-black" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
          data-testid="poster-type-video"
        >
          <Video size={14} /> Video Poster
        </button>
      </div>

      {/* Current preview */}
      <PosterPreview poster={poster} />

      {/* Upload / Input */}
      <div className="space-y-4">
        {poster.media_type === "image" ? (
          <div>
            <Label className="text-sm font-medium flex items-center gap-2 mb-2"><Upload size={14} /> Upload Photo Poster</Label>
            <Input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={e => { if (e.target.files[0]) uploadFile(e.target.files[0]); }}
              className="rounded-xl"
              disabled={uploading}
              data-testid="poster-upload-input"
            />
            {uploading && <p className="text-xs text-[#E8922F] mt-1">Uploading...</p>}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium flex items-center gap-2 mb-2"><Upload size={14} /> Upload Video File</Label>
              <Input
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={e => { if (e.target.files[0]) uploadFile(e.target.files[0]); }}
                className="rounded-xl"
                disabled={uploading}
                data-testid="poster-video-upload"
              />
              {uploading && <p className="text-xs text-[#E8922F] mt-1">Uploading...</p>}
            </div>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-gray-400">OR paste a YouTube link</span></div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">YouTube / Video URL</Label>
              <Input
                value={poster.video_url || ""}
                onChange={e => setPoster({ ...poster, video_url: e.target.value })}
                className="rounded-lg mt-1 text-sm"
                placeholder="https://youtube.com/watch?v=..."
                data-testid="poster-video-url"
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-gray-500">Description</Label>
            <Input
              value={poster.alt_text}
              onChange={e => setPoster({ ...poster, alt_text: e.target.value })}
              className="rounded-lg mt-1 text-sm"
              placeholder="e.g. Concert on Sept 20th"
              data-testid="poster-alt-input"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Link (when clicked)</Label>
            <Input
              value={poster.link}
              onChange={e => setPoster({ ...poster, link: e.target.value })}
              className="rounded-lg mt-1 text-sm"
              placeholder="/events"
              data-testid="poster-link-input"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Background Color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                value={poster.bg_color}
                onChange={e => setPoster({ ...poster, bg_color: e.target.value })}
                className="w-10 h-9 rounded border cursor-pointer"
                data-testid="poster-color-input"
              />
              <Input
                value={poster.bg_color}
                onChange={e => setPoster({ ...poster, bg_color: e.target.value })}
                className="rounded-lg text-sm flex-1"
              />
            </div>
          </div>
        </div>

        <Button onClick={saveSettings} className="btn-primary text-sm" disabled={saving} data-testid="save-poster-btn">
          <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save Poster Settings"}
        </Button>
      </div>
    </div>
  );
}
